/**
 * Role Service for managing user roles within organizations.
 *
 * Roles are stored in Hugo_ToolAccess/{uid}.roles map:
 *   roles: {
 *     [organizationId]: {
 *       role: string,       // 'org_admin' | 'site_manager' | 'department_lead' | 'team_lead' | 'respondent'
 *       scopeId: string,    // ID of the site/department/team (null for org_admin/respondent)
 *       scopeType: string,  // 'organization' | 'site' | 'department' | 'team'
 *       assignedAt: Timestamp,
 *       assignedBy: string  // UID of the user who assigned the role
 *     }
 *   }
 *
 * Available roles (ordered by access level):
 *   - org_admin: Full access to the organization
 *   - site_manager: Manages a specific site
 *   - department_lead: Leads a specific department
 *   - team_lead: Leads a specific team
 *   - respondent: Can only take surveys (default)
 */
import { db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  Timestamp,
  arrayRemove,
  deleteField,
} from 'firebase/firestore';
import { collections } from '../utils/firestore';

/**
 * Role definitions with labels and scope requirements
 */
/**
 * The ONE canonical role set (Beta-1 plan P1-04). Keys are the stored role ids and never
 * change; `label` is what every screen and email shows (Tim's titles, D08). `scopeLabel`
 * is the human name of the scope the role is attached to. Do not copy these labels
 * anywhere else — import ROLE_DEFINITIONS / roleLabel() instead.
 */
export const ROLE_DEFINITIONS = {
  org_admin: {
    label: 'Org Admin',
    description: 'Full access to all organization data and settings',
    scopeType: 'organization',
    scopeLabel: 'organization',
    requiresScopeId: false,
  },
  site_manager: {
    label: 'Facility/Site/Plant Manager',
    description: 'Sees and manages one site/facility/plant',
    scopeType: 'site',
    scopeLabel: 'site',
    requiresScopeId: true,
  },
  department_lead: {
    label: 'Department Head',
    description: 'Sees one department; L2 reviewer for its OE Conditions',
    scopeType: 'department',
    scopeLabel: 'department',
    requiresScopeId: true,
  },
  team_lead: {
    label: 'Frontline Supervisor / Team Leader',
    description: 'Sees one Natural Work Team (NWT); L1 reviewer for its OE Conditions',
    scopeType: 'team',
    scopeLabel: 'Natural Work Team (NWT)',
    requiresScopeId: true,
  },
  respondent: {
    label: 'Individual Contributor',
    description: 'Documents OE Conditions and their remediation, takes surveys, and sees a snapshot of their Natural Work Team',
    scopeType: 'organization',
    scopeLabel: 'organization',
    requiresScopeId: false,
  },
  // Beta-1 P4-03/04 (View As order: Administrator → Executive → … → Org IT → PPI Tech Support)
  executive: {
    label: 'Executive',
    description: 'Enterprise-level view across all sites; can view as site managers and below',
    scopeType: 'organization',
    scopeLabel: 'organization',
    requiresScopeId: false,
  },
  org_it: {
    label: 'Org IT',
    description: 'Interfaces with organization software and databases; sees every member (support role)',
    scopeType: 'organization',
    scopeLabel: 'organization',
    requiresScopeId: false,
  },
};

/** Display label for a stored role key (falls back to the key itself). */
export function roleLabel(role) {
  return ROLE_DEFINITIONS[role]?.label || role || '';
}

/**
 * Get all available role keys
 */
export function getAvailableRoles() {
  return Object.keys(ROLE_DEFINITIONS);
}

/**
 * Assign a role to a user within an organization.
 * @param {string} uid - Target user UID
 * @param {string} organizationId - Organization ID
 * @param {string} role - Role key (org_admin, site_manager, etc.)
 * @param {string|null} scopeId - ID of the site/department/team (if scoped)
 * @param {string} assignedBy - UID of the user assigning the role
 */
export async function assignRole(uid, organizationId, role, scopeId, assignedBy, units = {}) {
  if (!ROLE_DEFINITIONS[role]) {
    throw new Error(`Invalid role: ${role}`);
  }

  const roleDef = ROLE_DEFINITIONS[role];
  if (roleDef.requiresScopeId && !scopeId) {
    throw new Error(`Role ${role} requires a scope ID (${roleDef.scopeType})`);
  }

  const docRef = doc(db, collections.toolAccess, uid);
  const docSnap = await getDoc(docRef);

  const existingData = docSnap.exists() ? docSnap.data() : { tools: {}, roles: {} };
  // P4-09 / P4-22: the member's units travel with the role (an invited IC lands in their NWT).
  const roleEntry = {
    role,
    scopeId: scopeId || null,
    scopeType: roleDef.scopeType,
    siteId: units.siteId || null,
    deptId: units.deptId || null,
    nwtId: units.nwtId || null,
    assignedAt: Timestamp.now(),
    assignedBy,
  };

  const updatedRoles = {
    ...(existingData.roles || {}),
    [organizationId]: roleEntry,
  };

  await setDoc(docRef, { roles: updatedRoles }, { merge: true });

  // Keep the org's membersIndex mirror in sync (clients list members from it).
  try {
    await setDoc(
      doc(db, collections.organizations, organizationId),
      {
        membersIndex: {
          [uid]: {
            email: existingData.email || null,
            displayName: existingData.displayName || null,
            firstName: existingData.firstName || null,
            lastName: existingData.lastName || null,
            title: existingData.title || null,
            role,
            scopeId: scopeId || null,
            scopeType: roleDef.scopeType,
            siteId: units.siteId || null,
            deptId: units.deptId || null,
            nwtId: units.nwtId || null,
          },
        },
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('Could not update org membersIndex:', err.message);
  }
  return roleEntry;
}

/**
 * Remove a user's role in an organization.
 * @param {string} uid - Target user UID
 * @param {string} organizationId - Organization ID
 */
export async function removeRole(uid, organizationId) {
  const docRef = doc(db, collections.toolAccess, uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return;

  const existingData = docSnap.data();
  const removedRole = (existingData.roles || {})[organizationId];
  const updatedRoles = { ...(existingData.roles || {}) };
  delete updatedRoles[organizationId];

  await updateDoc(docRef, { roles: updatedRoles });

  // org_admins also live in the org's `admins` array (visibility + update
  // rules) — revoke that too, so removal actually removes access.
  if (removedRole && removedRole.role === 'org_admin') {
    try {
      await updateDoc(doc(db, collections.organizations, organizationId), {
        admins: arrayRemove(uid),
      });
    } catch (err) {
      // Non-fatal: caller may lack permission on the org doc; role is gone either way.
      console.error('Could not remove uid from org admins array:', err);
    }
  }

  // Drop them from the org's membersIndex mirror too.
  try {
    await updateDoc(doc(db, collections.organizations, organizationId), {
      [`membersIndex.${uid}`]: deleteField(),
    });
  } catch (err) {
    console.warn('Could not update org membersIndex:', err.message);
  }
}

/**
 * Get a user's role in a specific organization.
 * @param {string} uid - User UID
 * @param {string} organizationId - Organization ID
 * @returns {Object|null} Role entry or null
 */
export async function getUserRole(uid, organizationId) {
  const docRef = doc(db, collections.toolAccess, uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return (data.roles || {})[organizationId] || null;
}

/** Convert an org doc's membersIndex map into the members array shape. */
export function membersFromOrgDoc(orgData) {
  const index = orgData?.membersIndex || {};
  return Object.entries(index).map(([uid, m]) => ({
    uid,
    email: m.email || null,
    displayName: m.displayName || null,
    firstName: m.firstName || null,
    lastName: m.lastName || null,
    title: m.title || null,
    photoURL: m.photoURL || null,
    role: m.role,
    scopeId: m.scopeId ?? null,
    scopeType: m.scopeType || null,
    scopeIds: Array.isArray(m.scopeIds) ? m.scopeIds : null,
    nwtId: m.nwtId || null,
    deptId: m.deptId || null,
    siteId: m.siteId || null,
  }));
}

/**
 * Get all users with roles in a specific organization.
 * Reads Hugo_Organizations/{orgId}.membersIndex — a mirror maintained by the
 * cloud functions (hugoCreateOrgUser / hugoClaimBetaInvite) and client role
 * writes. Hugo_ToolAccess docs are only readable by their owner + superadmins,
 * so scanning that collection only worked for superadmins.
 * @param {string} organizationId - Organization ID
 * @returns {Array} Array of { uid, email, displayName, role, scopeId, scopeType }
 */
export async function getOrganizationMembers(organizationId) {
  const orgSnap = await getDoc(doc(db, collections.organizations, organizationId));
  if (!orgSnap.exists()) return [];
  return membersFromOrgDoc(orgSnap.data());
}

/**
 * Subscribe to organization members in real-time.
 * Watches Hugo_Organizations/{orgId} and maps its membersIndex (see
 * getOrganizationMembers for why we don't scan Hugo_ToolAccess).
 * @param {string} organizationId - Organization ID
 * @param {Function} callback - Called with array of members
 * @returns {Function} Unsubscribe function
 */
export function subscribeToOrganizationMembers(organizationId, callback) {
  return onSnapshot(
    doc(db, collections.organizations, organizationId),
    (snapshot) => {
      callback(snapshot.exists() ? membersFromOrgDoc(snapshot.data()) : []);
    },
    (error) => {
      console.warn('Error subscribing to organization members:', error.message);
      callback([]);
    }
  );
}

/**
 * Check if a user has at least a certain role level in an organization.
 * Role hierarchy: org_admin > site_manager > department_lead > team_lead > respondent
 * (the ranking itself lives in ./roleHierarchy.js so Firebase-free modules can share it).
 */
export { ROLE_HIERARCHY, roleRank, hasMinimumRole } from './roleHierarchy';

/**
 * Store an email address on a Hugo_ToolAccess document for display purposes.
 * @param {string} uid - Document ID
 * @param {string} email - Email address to store
 */
export async function setToolAccessEmail(uid, email) {
  const docRef = doc(db, collections.toolAccess, uid);
  await setDoc(docRef, { email }, { merge: true });
}
